import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Pagination, Spinner, Alert } from 'react-bootstrap';
import { getAuditLogAction } from '../../Action/modernAdminActions';
import debounce from 'lodash.debounce';

const EVENT_TYPES = {
    "Authentication": "authentication",
    "Login": "login",
    "Logout": "logout",
    "Data Access": "data_access",
    "Data Create": "data_create",
    "Data Update": "data_update",
    "Data Delete": "data_delete",
    "Admin Action": "admin_action",
    "Permission Change": "permission_change",
    "System Event": "system_event",
};

const SEVERITY_LEVELS = {
    "Info": "info",
    "Warning": "warning",
    "Error": "error",
    "Critical": "critical"
};

const LogHistory = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [filters, setFilters] = useState({
        user_id: '',
        resource_type: '',
        event_type: '',
        search: '',
        start_date: '',
        end_date: '',
    });
    const [pagination, setPagination] = useState({
        page: 1,
        page_size: 15,
        total: 0,
        total_pages: 0,
    });

    const fetchLogs = useCallback(async (currentFilters, currentPage) => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                ...currentFilters,
                page: currentPage,
                page_size: pagination.page_size,
            };
            const res = await getAuditLogAction(params);
            if (res && res.entries) {
                setLogs(res.entries);
                setPagination(prev => ({
                    ...prev,
                    total: res.total,
                    total_pages: res.total_pages,
                    page: res.page,
                }));
            } else {
                setLogs([]);
                setPagination(prev => ({ ...prev, total: 0, total_pages: 0, page: 1 }));
            }
        } catch (err) {
            setError('Failed to fetch audit logs. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [pagination.page_size]);

    useEffect(() => {
        fetchLogs(filters, pagination.page);
    }, [pagination.page]);

    const debouncedFetch = useCallback(debounce((newFilters) => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchLogs(newFilters, 1);
    }, 500), [fetchLogs]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const newFilters = { ...filters, [name]: value };
        setFilters(newFilters);
        debouncedFetch(newFilters);
    };
    
    const handlePageChange = (pageNumber) => {
        setPagination(prev => ({ ...prev, page: pageNumber }));
    };

    return (
        <Container fluid className="p-4">
            <Row>
                <Col>
                    <h2 className="font-weight-bold">Audit Log History</h2>
                    <p>Track all system and user activities.</p>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Header>
                    <strong>Filter Logs</strong>
                </Card.Header>
                <Card.Body>
                    <Form>
                        <Row>
                            {/* Placeholder for filter inputs */}
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Search</Form.Label>
                                    <Form.Control type="text" name="search" placeholder="Action, Description, IP..." onChange={handleFilterChange} />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Event Type</Form.Label>
                                    <Form.Control as="select" name="event_type" onChange={handleFilterChange} value={filters.event_type}>
                                        <option value="">All</option>
                                        {Object.entries(EVENT_TYPES).map(([key, value]) => <option key={value} value={value}>{key}</option>)}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Severity</Form.Label>
                                    <Form.Control as="select" name="severity" onChange={handleFilterChange} value={filters.severity}>
                                        <option value="">All</option>
                                        {Object.entries(SEVERITY_LEVELS).map(([key, value]) => <option key={value} value={value}>{key}</option>)}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control type="date" name="start_date" onChange={handleFilterChange} value={filters.start_date} />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control type="date" name="end_date" onChange={handleFilterChange} value={filters.end_date} />
                                </Form.Group>
                            </Col>
                            <Col md={1} className="d-flex align-items-end">
                                <Button variant="secondary" onClick={() => {
                                    setFilters({ user_id: '', resource_type: '', event_type: '', search: '', start_date: '', end_date: '' });
                                    fetchLogs({}, 1);
                                }}>Reset</Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    {loading ? (
                        <div className="text-center">
                            <Spinner animation="border" role="status">
                                <span className="sr-only">Loading...</span>
                            </Spinner>
                        </div>
                    ) : error ? (
                        <Alert variant="danger">{error}</Alert>
                    ) : logs.length === 0 ? (
                        <Alert variant="info">No audit logs found for the selected filters.</Alert>
                    ) : (
                        <>
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>User</th>
                                        <th>Action</th>
                                        <th>Resource</th>
                                        <th>Description</th>
                                        <th>IP Address</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map(log => (
                                        <React.Fragment key={log.audit_id}>
                                            <tr onClick={() => setExpandedRow(expandedRow === log.audit_id ? null : log.audit_id)} style={{ cursor: 'pointer' }}>
                                                <td>{new Date(log.timestamp).toLocaleString('en-GB')}</td>
                                                <td>{log.user_name || log.user_id}</td>
                                                <td>{log.action}</td>
                                                <td>{log.resource_type ? `${log.resource_type}: ${log.resource_id}` : 'N/A'}</td>
                                                <td>{log.description}</td>
                                                <td>{log.ip_address}</td>
                                                <td>
                                                    <Button variant="link" size="sm">
                                                        {expandedRow === log.audit_id ? 'Collapse' : 'Expand'}
                                                    </Button>
                                                </td>
                                            </tr>
                                            {expandedRow === log.audit_id && (
                                                <tr>
                                                    <td colSpan="7">
                                                        <Card className="m-2">
                                                            <Card.Body>
                                                                <pre>{JSON.stringify(log, null, 2)}</pre>
                                                            </Card.Body>
                                                        </Card>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </Table>
                            {renderPaginationControls()}
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );

    function renderPaginationControls() {
        const { page, total_pages } = pagination;
        if (!total_pages || total_pages <= 1) return null;

        let items = [];
        // Previous page link
        items.push(<Pagination.Prev key="prev" onClick={() => handlePageChange(page - 1)} disabled={page === 1} />);

        // Page numbers
        const pageBuffer = 2;
        const startPage = Math.max(1, page - pageBuffer);
        const endPage = Math.min(total_pages, page + pageBuffer);

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>{1}</Pagination.Item>);
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === page} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < total_pages) {
            if (endPage < total_pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(<Pagination.Item key={total_pages} onClick={() => handlePageChange(total_pages)}>{total_pages}</Pagination.Item>);
        }

        // Next page link
        items.push(<Pagination.Next key="next" onClick={() => handlePageChange(page + 1)} disabled={page === total_pages} />);
        
        return <Pagination className="justify-content-end">{items}</Pagination>;
    }
};

export default LogHistory;
